import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHYSICIAN = "Physician Practice — Mobile Echocardiography";
const COMMUNITY = "Community Heart Health / Screening Event";
const KYH = "Know Your Heart — Individual Screening";
const GENERAL = "General Inquiry";

const SERVICE_OPTIONS = [PHYSICIAN, COMMUNITY, KYH, GENERAL];

// Which inquiry types route to the Community Screening Coordinator (Jennifer)
const COMMUNITY_OPTIONS = [COMMUNITY, KYH];

const KYH_LOCATIONS = ["Sugar Land", "Cypress"];

const EMPTY = {
  name: "",
  organization: "",
  email: "",
  phone: "",
  service_type: SERVICE_OPTIONS[0],
  message: "",
  event_date: "",
  attendance: "",
  venue: "",
  preferred_location: "Sugar Land",
  preferred_date: "",
  preferred_time: "",
  scheduling_notes: "",
};

export default function ContactForm({ presetService = null, presetSignal = 0, onServiceChange }) {
  const [form, setForm] = useState({ ...EMPTY });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Apply a service preset requested from outside (CTA buttons)
  useEffect(() => {
    if (presetService && SERVICE_OPTIONS.includes(presetService)) {
      setForm((f) => ({ ...f, service_type: presetService }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetSignal]);

  // Report the currently selected service up so the section heading can adapt
  useEffect(() => {
    if (onServiceChange) onServiceChange(form.service_type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.service_type]);

  const isPhysician = form.service_type === PHYSICIAN;
  const isCommunity = form.service_type === COMMUNITY;
  const isKYH = form.service_type === KYH;
  const isGeneral = form.service_type === GENERAL;

  const submitLabel = isCommunity
    ? "Request Event Information"
    : isKYH
    ? "Request an Appointment"
    : isGeneral
    ? "Send Inquiry"
    : "Request On-Site Service";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setLoading(true);
    try {
      const inquiry_type = COMMUNITY_OPTIONS.includes(form.service_type) ? "community" : "general";
      await axios.post(`${API}/leads`, { ...form, inquiry_type });
      setDone(true);
      toast.success("Request received — we'll be in touch shortly.");
      setForm({ ...EMPTY, service_type: form.service_type });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-success" className="bg-white border border-border rounded-2xl p-10 text-center">
        <CheckCircle2 className="mx-auto text-primary mb-4" size={56} />
        <h3 className="font-heading text-2xl text-secondary mb-2">Thank you</h3>
        <p className="text-muted-foreground mb-6">Your request has been received. Our team will reach out to follow up shortly.</p>
        <button data-testid="contact-another-button" onClick={() => setDone(false)} className="rounded-full border border-primary text-primary font-bold px-6 py-2.5 hover:bg-primary hover:text-white transition-colors duration-200">
          Submit another request
        </button>
      </div>
    );
  }

  const inputCls = "w-full rounded-lg border border-input bg-white px-4 py-3 text-sm text-secondary placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow duration-200";
  const labelCls = "block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2";

  const showOrganization = isPhysician || isCommunity;
  const orgLabel = isCommunity ? "Organization / Event Name" : "Practice / Organization";
  const showMessage = isPhysician || isCommunity || isGeneral;
  const messagePlaceholder = isCommunity
    ? "Anything else you'd like us to know about your event."
    : "Tell us about your practice, patient volume, or scheduling needs.";

  return (
    <form data-testid="contact-form" onSubmit={submit} className="bg-white border border-border rounded-2xl p-6 sm:p-8 space-y-5">
      {/* Name + Organization */}
      <div className={showOrganization ? "grid sm:grid-cols-2 gap-5" : ""}>
        <div>
          <label className={labelCls}>Full Name *</label>
          <input data-testid="contact-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Smith" />
        </div>
        {showOrganization && (
          <div>
            <label className={labelCls}>{orgLabel}</label>
            <input data-testid="contact-organization" className={inputCls} value={form.organization} onChange={set("organization")} placeholder={isCommunity ? "Organization or event name" : "Houston Cardiology Group"} />
          </div>
        )}
      </div>

      {/* Email + Phone */}
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Email *</label>
          <input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
        </div>
        <div>
          <label className={labelCls}>Phone *</label>
          <input data-testid="contact-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="832-000-0000" />
        </div>
      </div>

      {/* Service Needed */}
      <div>
        <label className={labelCls}>Service Needed</label>
        <select data-testid="contact-service" className={inputCls} value={form.service_type} onChange={set("service_type")}>
          {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Community-event specific fields */}
      {isCommunity && (
        <>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Preferred Event Date</label>
              <input data-testid="contact-event-date" className={inputCls} value={form.event_date} onChange={set("event_date")} placeholder="e.g., Saturday, Nov 15, 2026" />
            </div>
            <div>
              <label className={labelCls}>Estimated Attendance</label>
              <input data-testid="contact-attendance" className={inputCls} value={form.attendance} onChange={set("attendance")} placeholder="e.g., 75–100 people" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Available Space / Venue Information</label>
            <input data-testid="contact-venue" className={inputCls} value={form.venue} onChange={set("venue")} placeholder="e.g., private classroom, fellowship hall, conference room" />
          </div>
        </>
      )}

      {/* Know Your Heart — individual screening fields */}
      {isKYH && (
        <>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Preferred Location</label>
              <select data-testid="contact-location-pref" className={inputCls} value={form.preferred_location} onChange={set("preferred_location")}>
                {KYH_LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Preferred Date</label>
              <input data-testid="contact-preferred-date" className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} placeholder="e.g., Saturday, Nov 15, 2026" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Preferred Time</label>
            <input data-testid="contact-preferred-time" className={inputCls} value={form.preferred_time} onChange={set("preferred_time")} placeholder="e.g., Morning, or 10:00 AM" />
          </div>
          <div>
            <label className={labelCls}>Additional Scheduling Notes (optional)</label>
            <textarea data-testid="contact-notes" rows={3} className={inputCls} value={form.scheduling_notes} onChange={set("scheduling_notes")} placeholder="Anything else that helps us schedule your screening." />
          </div>
        </>
      )}

      {/* Message (physician / community / general) */}
      {showMessage && (
        <div>
          <label className={labelCls}>Message</label>
          <textarea data-testid="contact-message" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder={messagePlaceholder} />
        </div>
      )}

      <button
        data-testid="request-service-submit"
        type="submit"
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-bold text-white hover:bg-secondary transition-colors duration-200 disabled:opacity-70"
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
        {loading ? "Sending..." : submitLabel}
      </button>

      {isKYH && (
        <p data-testid="kyh-request-disclaimer" className="text-xs text-muted-foreground leading-relaxed">
          Submitting this form is a request only. Your preferred date and time are not confirmed
          until Cardiac Precision Diagnostics contacts you to confirm availability.
        </p>
      )}
    </form>
  );
}
